import { gsap } from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
import SplitText from 'gsap/SplitText';
import GSDevTools from 'gsap/GSDevTools';

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger, SplitText, GSDevTools);

// Function to animate .btn-icon
function animateBtn(target, timeline) {
    const icons = target.querySelectorAll('.icon');

    const tl = gsap.timeline();
    tl.fromTo(
        target,
        { clipPath: 'polygon(0% 100%, 0% 100%, 0% 0%, 0% 0%)', opacity: 0 },
        { clipPath: 'polygon(0% 100%, 100% 100%, 100% 0%, 0% 0%)', opacity: 1, ease: 'power2.out', duration: 0.25 },
    );

    // Animate icons within the button
    tl.fromTo(icons, { opacity: 0, x: -8 }, { opacity: 1, x: 0, ease: 'sine.inOut', duration: 0.05 }, '+=0.15');
    return tl;
}

// Function to slide up words using SplitText
function slideUpWords(element, timeline) {
    if (!element) return;

    const split = new SplitText(element, { type: 'words, lines', linesClass: 'line', wordsClass: 'word' });
    const words = split.words;
    const lines = split.lines;

    // Calculate and apply height to lines
    lines.forEach((line) => {
        const lineWords = line.querySelectorAll('.word');
        let maxHeight = 0;
        lineWords.forEach((word) => {
            const wordHeight = word.offsetHeight;
            if (wordHeight > maxHeight) {
                maxHeight = wordHeight;
            }
        });
        line.style.overflow = 'hidden';
        line.style.height = `${maxHeight}px`;
    });

    const tl = gsap.timeline();
    tl.from(words, { opacity: 0, y: 100, duration: 0.7, ease: 'sine.inOut', stagger: 0.05 });
    return tl;
}

// Function to slide up lines
function slideUpLines(element, timeline) {
    if (!element) return;

    const split = new SplitText(element, { type: 'lines', linesClass: 'line' });
    const lines = split.lines;

    if (element.parentElement) {
        element.parentElement.style.overflow = 'hidden';
    }

    const tl = gsap.timeline();
    tl.from(lines, { opacity: 0, y: 50, duration: 0.5, ease: 'sine.inOut', stagger: 0.05 });
    return tl;
}

// Function to perform swipe right animation
function swipeRight(target, timeline) {
    const tl = gsap.timeline();
    tl.fromTo(
        target,
        { clipPath: 'polygon(0% 100%, 0% 100%, 0% 0%, 0% 0%)', opacity: 0 },
        { clipPath: 'polygon(0% 100%, 100% 100%, 100% 0%, 0% 0%)', opacity: 1, ease: 'power2.out', duration: 0.75 },
    );
    return tl;
}

// Function to perform swipe left animation
function swipeLeft(target, timeline) {
    const tl = gsap.timeline();
    tl.fromTo(
        target,
        { clipPath: 'polygon(100% 0%, 100% 0%, 100% 100%, 100% 100%)', opacity: 0 },
        { clipPath: 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)', opacity: 1, ease: 'power2.out', duration: 0.75 },
    );
    return tl;
}

// Function to perform swipe up animation
function swipeUp(target, timeline) {
    const tl = gsap.timeline();
    tl.fromTo(
        target,
        { clipPath: 'polygon(0 100%, 100% 100%, 100% 100%, 0 100%)', opacity: 0 },
        { clipPath: 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)', opacity: 1, ease: 'power2.out', duration: 0.75 },
    );
    return tl;
}

// Function to perform swipe down animation
function swipeDown(target, timeline) {
    const tl = gsap.timeline();
    tl.fromTo(
        target,
        { clipPath: 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)', opacity: 0 },
        { clipPath: 'polygon(0 0, 100% 0, 100% 100%, 0% 100%)', opacity: 1, ease: 'power2.out', duration: 0.75 },
    );
    return tl;
}

// Hero Section
const hero = document.querySelector('.js-hero');
if (hero) {
    const h1 = hero.querySelector('h1');
    const h2 = hero.querySelector('h2');
    const date = hero.querySelector('time');
    const p = hero.querySelector('p');
    const btn = hero.querySelector('.btn');

    const tlHero = gsap.timeline({ delay: 0.15 });

    if (h1) tlHero.add(slideUpWords(h1, tlHero));
    if (h2) tlHero.add(slideUpWords(h2, tlHero), '>-0.3');
    if (date) tlHero.add(slideUpWords(date, tlHero), '>-0.3');
    if (p) tlHero.add(slideUpLines(p, tlHero), '>-0.3');
    if (btn) tlHero.add(animateBtn(btn, tlHero), '>-0.3');
}

// Statistic Cards
document.addEventListener('DOMContentLoaded', () => {
    const statistics = document.querySelector('.js-statistic-cards');
    if (!statistics) return;

    const numbers = statistics.querySelectorAll('.data-number');
    const card = statistics.querySelectorAll('.stat-card');

    const getDecimalPlaces = (value) => {
        if (!value || isNaN(value)) return 0;
        if (Math.floor(value) === value) return 0;
        return value.toString().split('.')[1]?.length || 0;
    };

    const tlNumbers = gsap.timeline({
        scrollTrigger: {
            trigger: card,
            start: 'top 90%',
            toggleActions: 'play none none none',
        },
    });

    numbers.forEach((el, index) => {
        const target = { val: 0 };
        const finalValue = parseFloat(el.getAttribute('data-number'));
        const decimalPlaces = getDecimalPlaces(finalValue);

        tlNumbers.to(
            target,
            {
                val: finalValue,
                duration: 3,
                ease: 'power3.out',
                onUpdate: () => {
                    el.innerText = target.val.toFixed(decimalPlaces);
                },
            },
            index * 0.5,
        );
    });
});

// CTA Cards
const ctaCards = document.querySelector('.js-cta-cards');
if (ctaCards) {
    const cards = Array.from(ctaCards.querySelectorAll('.cta-card'));
    const tlCards = gsap.timeline({
        scrollTrigger: {
            trigger: ctaCards,
            start: 'top 90%',
            once: true,
            toggleActions: 'play none none none',
        },
    });

    cards.forEach((card, index) => {
        const borderEl = card.querySelector('.border-span');
        const hdrEl = card.querySelector('h3');
        const subHdrEl = card.querySelector('h5');
        const textEl = card.querySelector('p');
        const btn = card.querySelector('.btn');
        const btnIcon = card.querySelector('.btn-icon');

        if (borderEl) tlCards.add(swipeRight(borderEl, tlCards), index * 0.2);
        if (hdrEl) tlCards.add(slideUpWords(hdrEl, tlCards), '>-0.15');
        if (subHdrEl) tlCards.add(slideUpWords(subHdrEl, tlCards), '>-0.15');
        if (textEl) tlCards.add(slideUpLines(textEl, tlCards), '>-0.15');
        if (btn) tlCards.add(animateBtn(btn, tlCards), '>-0.3');
        if (btnIcon) tlCards.add(animateBtn(btnIcon, tlCards), '>-0.3');
    });
}

/**
 * Our Culture Grid
 */
const grid = document.querySelector('.js-culture-grid');
if (grid) {
    const items = Array.from(grid.querySelectorAll('.grid__item'));

    const tlGrid = gsap.timeline({
        scrollTrigger: {
            trigger: grid,
            start: 'top 90%',
            end: 'bottom 10%',
            once: true,
            toggleActions: 'play none none none',
        },
    });

    // Loop through each item and add animations to the timeline with stagger
    items.forEach((item, index) => {
        const imageEl = item.querySelector('.item__image');
        const hdrEl = item.querySelector('.item__hdr');
        const textEl = item.querySelectorAll('p');

        if (!imageEl) return;

        if (item.classList.contains('item-1')) tlGrid.add(swipeLeft(imageEl, tlGrid), index * 0.5);
        if (item.classList.contains('item-2')) tlGrid.add(swipeRight(imageEl, tlGrid), index * 0.5);
        if (item.classList.contains('item-3')) tlGrid.add(swipeLeft(imageEl, tlGrid), index * 0.5);

        if (hdrEl) tlGrid.add(slideUpWords(hdrEl, tlGrid), '>-0.3');
        if (textEl) tlGrid.add(slideUpLines(textEl, tlGrid), '>-0.3');
    });
}

// Animate the highlights
const highlightsContainer = document.querySelector('.js-highlights');

if (highlightsContainer) {
    // Select all highlight elements within the container
    const highlights = Array.from(highlightsContainer.querySelectorAll('.highlight'));

    // Reverse the order to animate from last to first
    highlights.reverse();

    // Create the GSAP timeline for the highlights
    const tlHighlights = gsap.timeline({
        scrollTrigger: {
            trigger: highlightsContainer,
            start: 'top 90%',
            end: 'bottom 10%',
            once: true,
            toggleActions: 'play none none none',
        },
    });

    highlights.forEach((highlight, index) => {
        const textEl = highlight.querySelector('.highlight__text');
        const hdrEl = highlight.querySelector('.highlight__hdr');

        tlHighlights.add(swipeUp(highlight, tlHighlights), index * 0.2);
        if (hdrEl) tlHighlights.add(slideUpWords(hdrEl, tlHighlights), '>-0.3');
    });
}
